import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { faChevronLeft, faChevronRight } from '@fortawesome/pro-light-svg-icons';

import { IconButton } from '@spacefill/uikit/src/components/IconButton';

import { AiChatChoice } from '../../../components/ai-chat/AiChatChoice';
import { AiChatOtherAnswer } from '../../../components/ai-chat/AiChatOtherAnswer';

export const AiSuggestionPicker = ({ questions, onSend }) => {
    const { t } = useTranslation();
    const [pageIndex, setPageIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [otherAnswers, setOtherAnswers] = useState({});
    const hasPages = questions.length > 1;

    if (questions.length === 0) {
        return null;
    }

    const question = questions[pageIndex];

    const getOtherAnswer = (index) => {
        const text = (otherAnswers[index] ?? '').trim();
        if (!text) {
            return undefined;
        }

        return hasPages ? t('{{question}} → {{answer}}', { question: questions[index].text, answer: text }) : text;
    };

    const answer = (message) => {
        const nextAnswers = { ...answers, [pageIndex]: message };
        const resolvedAnswers = questions.map((_question, index) => nextAnswers[index] ?? getOtherAnswer(index));
        const nextPageIndex = findNextUnansweredIndex(resolvedAnswers, pageIndex);
        setAnswers(nextAnswers);

        if (nextPageIndex === -1) {
            onSend(resolvedAnswers.join('\n'));
            return;
        }

        setPageIndex(nextPageIndex);
    };

    return (
        <div className='mt-2 -mx-1 flex flex-col gap-1'>
            {hasPages && (
                <div className='flex items-start gap-3 px-1'>
                    <p className='min-w-0 flex-1 font-medium wrap-anywhere'>{question.text}</p>
                    <div className='flex shrink-0 items-center gap-1 text-xs text-gray-500'>
                        <IconButton
                            icon={faChevronLeft}
                            label={t('Previous')}
                            variant='transparent'
                            onClick={() => setPageIndex(pageIndex - 1)}
                            disabled={pageIndex === 0}
                        />
                        <span>
                            {t('{{current}} of {{total}}', { current: pageIndex + 1, total: questions.length })}
                        </span>
                        <IconButton
                            icon={faChevronRight}
                            label={t('Next')}
                            variant='transparent'
                            onClick={() => setPageIndex(pageIndex + 1)}
                            disabled={pageIndex === questions.length - 1}
                        />
                    </div>
                </div>
            )}
            <div className='flex flex-col divide-y divide-gray-100'>
                {question.choices.map((choice, index) => (
                    <div
                        key={choice.message}
                        className='py-0.5'
                    >
                        <AiChatChoice
                            number={index + 1}
                            label={choice.label}
                            isSelected={answers[pageIndex] === choice.message}
                            onSelect={() => answer(choice.message)}
                        />
                    </div>
                ))}
                <div className='py-0.5'>
                    <AiChatOtherAnswer
                        key={pageIndex}
                        value={otherAnswers[pageIndex] ?? ''}
                        placeholder={t('Other')}
                        onChange={(value) => setOtherAnswers({ ...otherAnswers, [pageIndex]: value })}
                        onSubmit={() => answer(getOtherAnswer(pageIndex))}
                    />
                </div>
            </div>
        </div>
    );
};

const findNextUnansweredIndex = (answers, fromIndex) => {
    const indexes = answers.map((_answer, index) => (fromIndex + 1 + index) % answers.length);

    return indexes.find((index) => answers[index] === undefined) ?? -1;
};
